// Default tag mapping for video categories (fallback)
// Order: All, HIIT, Arms & Back, Lower Body, Shoulders & Core, Full Body, Abs
export const DEFAULT_TAG_MAPPING = {
    'All': [],
    'HIIT': ['hiit'],
    'Arms & Back': ['arms_&_back'],
    'Lower Body': ['lower_body'],
    'Shoulders & Core': ['shoulders_&_core'],
    'Full Body': ['full_body'],
    'Abs': ['abs']
};

// Build tag mapping from categories data or use default
export function buildTagMapping(categories = []) {
    if (!categories || categories.length === 0) {
        return DEFAULT_TAG_MAPPING;
    }
    
    // Sort categories by order field if it exists, otherwise keep original order
    const sortedCategories = [...categories].sort((a, b) => {
        const orderA = a.order !== undefined ? parseInt(a.order) : 999;
        const orderB = b.order !== undefined ? parseInt(b.order) : 999;
        return orderA - orderB;
    });
    
    const mapping = { 'All': [] };
    
    sortedCategories.forEach(category => {
        const name = category.name || category.handle;
        const tags = category.tags || [category.handle];
        mapping[name] = Array.isArray(tags) ? tags : tags.split(',').map(t => t.trim().toLowerCase());
    });
    
    return mapping;
}

// Get sorted category names for display
export function getCategoryNames(categories = []) {
    if (!categories || categories.length === 0) {
        return Object.keys(DEFAULT_TAG_MAPPING);
    }
    
    // Sort by order field if exists
    const sortedCategories = [...categories].sort((a, b) => {
        const orderA = a.order !== undefined ? parseInt(a.order) : 999;
        const orderB = b.order !== undefined ? parseInt(b.order) : 999;
        return orderA - orderB;
    });
    
    return ['All', ...sortedCategories.map(cat => cat.name || cat.handle)];
}

// Parse video tags from various formats
export function parseVideoTags(tags) {
    if (!tags) return [];
    
    // If already an array, just normalize to lowercase
    if (Array.isArray(tags)) {
        return tags.map(tag => tag.trim().toLowerCase());
    }
    
    try {
        let tagsString = tags;

        // Handle escaped JSON string like "[\"abs\"]"
        if (typeof tagsString === 'string' && tagsString.includes('\\')) {
            tagsString = tagsString.replace(/^"(.*)"$/, '$1');
            tagsString = tagsString.replace(/\\"/g, '"');
        }

        // Try parsing as JSON array
        const parsed = JSON.parse(tagsString);
        return Array.isArray(parsed) ? parsed.map(tag => tag.trim().toLowerCase()) : [];
    } catch (e) {
        // Fallback to comma-separated string
        return tags.split(',').map(tag => tag.trim().toLowerCase());
    }
}

// Check if video matches category
export function matchesCategory(videoTags, category, tagMapping = DEFAULT_TAG_MAPPING) {
    if (category === 'All' || category === 'all') {
        return true;
    }
    
    // First, check if the category directly matches any video tag (case-insensitive)
    const categoryLower = category.toLowerCase();
    if (videoTags.some(tag => tag.toLowerCase() === categoryLower)) {
        return true;
    }
    
    // Then check the tag mapping for more complex matches
    const tagsToMatch = tagMapping[category] || [];
    return tagsToMatch.some(tag => videoTags.includes(tag.toLowerCase()));
}

// Filter and sort videos
export function filterAndSortVideos(videos, currentCategory, sortOrder, maxVideos = 30, tagMapping = DEFAULT_TAG_MAPPING, filterByCurrentMonth = false) {
    // Use UTC to avoid timezone issues
    const today = new Date();
    const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-indexed (0 = January, 11 = December)
    
    // Filter videos
    const filteredVideos = videos.filter((video) => {
        // Parse as UTC to avoid timezone shifts
        const [year, month, day] = video.publish_date.split('-').map(Number);
        const publishDateUTC = Date.UTC(year, month - 1, day);
        const isPublished = publishDateUTC <= todayUTC;

        // Check if video is in current month (if filtering is enabled)
        let isCurrentMonth = true;
        if (filterByCurrentMonth) {
            isCurrentMonth = (year === currentYear && (month - 1) === currentMonth);
        }

        const videoTags = parseVideoTags(video.tags);
        const matchesTag = matchesCategory(videoTags, currentCategory, tagMapping);
        
        // Default to active if status is not set
        const isActive = !video.status || video.status.toLowerCase() === 'active';

        return matchesTag && isPublished && isCurrentMonth && isActive;
    });
    
    console.log('✅ Filtered videos count:', filteredVideos.length, 'from', videos.length, filterByCurrentMonth ? '(current month only)' : '');

    // Sort videos by date only (ignore time)
    const sortedVideos = [...filteredVideos].sort((a, b) => {
        const dateA = new Date(a.publish_date + 'T00:00:00');
        const dateB = new Date(b.publish_date + 'T00:00:00');

        return sortOrder === 'oldest' ? dateA - dateB : dateB - dateA;
    });

    // Limit videos
    return sortedVideos.slice(0, maxVideos);
}
